import { useState, useCallback, useMemo } from 'react'
import { useWebSocket } from './useWebSocket'
import { logger } from '@magnetar/utils'

export interface SupplierDeviceStats {
  totalConnectedDevices: number
  totalDisconnectedDevices: number
  connectedClinicsCount: number
  totalClinicsCount: number
  clinicIds: number[]
  clinicStats?: Array<{
    clinicId: number
    connectedDevices: number
    totalDevices: number
    hasOnlineDevices: boolean
    clinicName: string
    broadcastDevices: Array<{
      broadcast_device_id: number
      name: string
      is_active: boolean
      broadcast_type: string
      last_connected_at?: string | null
    }>
    onlineDeviceIds: number[]
    /** 設備 ID 到最後連線時間的映射 */
    deviceLastConnectedMap?: Record<number, string | null>
  }>
  timestamp: string
}

export interface UseSupplierDeviceStatsReturn {
  stats: SupplierDeviceStats | null
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  reconnect: () => void
}

type SupplierClinicStat = NonNullable<SupplierDeviceStats['clinicStats']>[number]

interface SupplierDeviceStatsMessage {
  type?: string
  success?: boolean
  data: SupplierDeviceStats & { message?: string }
}

export function useSupplierDeviceStats(supplierId: string | number): UseSupplierDeviceStatsReturn {
  const [stats, setStats] = useState<SupplierDeviceStats | null>(null)
  const [error, setError] = useState<string | null>(null)

  const wsProtocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsHost = import.meta.env.VITE_BROADCAST_SERVICE_URL || 'localhost:9010'
  const wsUrl = `${wsProtocol}//${wsHost}/admin/ws/supplier-device-stats?supplier_id=${supplierId}`

  const connectionKey = useMemo(() => `supplier-device-stats-${supplierId}`, [supplierId])

  const handleMessage = useCallback((raw: unknown) => {
    try {
      const message = raw as SupplierDeviceStatsMessage
      if (message.type === 'supplier_device_stats') {
        const rawData = message.data

        // 在前端重新計算連線和未連線裝置總數
        let totalConnectedDevices = 0
        let totalDisconnectedDevices = 0
        let connectedClinicsCount = 0

        if (rawData.clinicStats && rawData.clinicStats.length > 0) {
          rawData.clinicStats.forEach((clinic: SupplierClinicStat) => {
            totalConnectedDevices += clinic.connectedDevices || 0
            totalDisconnectedDevices += clinic.totalDevices - clinic.connectedDevices || 0

            if (clinic.hasOnlineDevices) {
              connectedClinicsCount++
            }
          })
        }

        // 重新組裝數據
        const processedStats: SupplierDeviceStats = {
          ...rawData,
          totalConnectedDevices,
          totalDisconnectedDevices,
          connectedClinicsCount,
        }

        setStats(processedStats)
        setError(null)
        logger.log('收到供應商設備狀態更新:', processedStats)
      } else if (message.type === 'error') {
        logger.error('WebSocket 錯誤:', message.data)
        setError(message.data.message || '連接錯誤')
      } else if (message.success === true) {
        logger.log('WebSocket 連接成功:', message.data?.message)
      }
    } catch (err) {
      logger.error('處理 WebSocket 訊息失敗:', err)
      setError('數據處理失敗')
    }
  }, [])

  const { isConnected, isConnecting, reconnect, status } = useWebSocket({
    connectionKey,
    url: wsUrl,
    enabled: !!supplierId,
    onMessage: handleMessage,
  })

  return {
    stats,
    isConnected,
    isConnecting,
    error: status === 'error' ? error || '連接失敗' : error,
    reconnect,
  }
}
